/**
 * Header Component
 * App header with title and actions
 */

import { MoonIcon, SunIcon, CoffeeIcon, HomeIcon, BudgetIcon, WalletIcon, ReportIcon, ProfileIcon } from '../icons/CategoryIcons';
import { useTheme } from '../../hooks/useUserPreferences';
import type { Page } from './BottomNav';

const BUY_ME_A_COFFEE_URL = 'https://ko-fi.com/nilocoffeetime';

// Get icon component for current page
const getPageIcon = (page: Page | undefined) => {
  const iconMap: Record<Page, React.ReactNode> = {
    'home': <HomeIcon className="w-5 h-5" />,
    'budget': <BudgetIcon className="w-5 h-5" />,
    'receitas': <WalletIcon className="w-5 h-5" />,
    'reports': <ReportIcon className="w-5 h-5" />,
    'profile': <ProfileIcon className="w-5 h-5" />,
    'balance': <HomeIcon className="w-5 h-5" />,
  };
  return page ? iconMap[page] : <HomeIcon className="w-5 h-5" />;
};

interface HeaderProps {
  title?: string | undefined;
  showHome?: boolean | undefined;
  showThemeToggle?: boolean | undefined;
  currentPage?: Page | undefined;
}

export function Header({ title = 'Equilíbrio Financeiro', showHome = true, showThemeToggle = true, currentPage }: HeaderProps) {
  const { isDark, toggleTheme, isUpdating } = useTheme();

  return (
    <header className={`sticky top-0 z-40 backdrop-blur-md border-b transition-colors duration-200 ${
      isDark
        ? 'bg-slate-950/90 border-slate-800/50'
        : 'bg-white/90 border-slate-200/50'
    }`}>
      <div className="flex items-center justify-between px-4 py-3 max-w-lg mx-auto">
        {showHome ? (
          <a
          href={BUY_ME_A_COFFEE_URL}
          target="_blank"
          rel="noopener noreferrer"
          title="Buy me a coffee"
            className={`p-2 rounded-lg transition-colors ${
              isDark
                ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid="home-button"
          >
            <CoffeeIcon className="w-5 h-5" />
          </a>
        ) : (
          <div className="w-9" />
        )}

        <div className="flex items-center gap-2">
          <span className="text-orange-500">
            {getPageIcon(currentPage)}
          </span>
          <h1 className={`text-base font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
            {title}
          </h1>
        </div>

        {showThemeToggle ? (
          <button
            onClick={toggleTheme}
            disabled={isUpdating}
            data-testid="theme-toggle"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className={`p-2 transition-colors ${
              isDark
                ? 'text-slate-400 hover:text-slate-300'
                : 'text-slate-500 hover:text-slate-700'
            } ${isUpdating ? 'opacity-50' : ''}`}
          >
            {isDark ? <MoonIcon className="w-5 h-5" /> : <SunIcon className="w-5 h-5 text-yellow-500" />}
          </button>
        ) : (
          <div className="w-9" />
        )}
      </div>
    </header>
  );
}
